import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@divlab/divanui';

import * as ApiOrder from '@Api/Order';
import * as ModalWindows from '@Stores/ModalWindows';
import { useAppConfig } from '@Contexts/AppConfig';
import useYandexPayInstance from '@Hooks/useYandexPayInstance';
import SkeletRect from '@Skelets/SkeletRect';
import SkeletWave from '@Skelets/SkeletWave';
import styles from './YandexSplitWidget.module.css';
import AdditionalPeriod from './elems/AdditionalPeriod';

import type { FC, HTMLAttributes } from 'react';
import type { CurrencyCode } from '@Types/Base';
import type { PaymentData, YandexWidgetType } from '@Types/YandexSplitWidget';
import type { AdditionalPeriodData } from './elems/AdditionalPeriod';

export interface YandexPayWidgetProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  cnSkeleton?: string;
  totalAmount: string;
  uuid?: string;
  type?: YandexWidgetType;
  splitUnavailableWarning?: React.ReactNode;
  currency?: CurrencyCode;
  additionalPeriod?: AdditionalPeriodData;
}

const YandexSplitWidget: FC<YandexPayWidgetProps> = (props) => {
  const {
    totalAmount,
    className,
    cnSkeleton,
    currency = 'RUB',
    uuid,
    splitUnavailableWarning,
    type = 'yandexSplit',
    additionalPeriod,
    ...restProps
  } = props;
  const { isProduction, yandexMerchantId } = useAppConfig();
  const [isMounted, setIsMounted] = useState(false);
  const [splitUnavailable, setSplitUnavailable] = useState(false);
  const { YaPayInstance } = useYandexPayInstance();
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetRef = useRef<Element | null>(null);
  const isWidgetVisible = isMounted && !splitUnavailable;
  const isButton = type === 'yandexCard';
  const isInfoType = type === 'splitInfo';
  const isInfoWidgetAvailable = isInfoType && isMounted && !splitUnavailable;

  const availablePaymentMethods: Array<'SPLIT' | 'CARD'> = useMemo(() => {
    switch (type) {
      case 'yandexCard': {
        return ['CARD', 'SPLIT'];
      }
      default: {
        return ['SPLIT'];
      }
    }
  }, [type]);

  const onPayButtonClick = useCallback(async () => {
    const resPay = await ApiOrder.getPay({ id: uuid });
    const url = resPay['bank-card-params']?.url;
    if (url) {
      return url;
    }
  }, [uuid]);

  const handleError = () => {
    ModalWindows.open('Info', { view: 'error', variant: 'unknown-error' });
    setSplitUnavailable(true);
  };

  const mountCorrectComponent = useCallback(
    (paymentSession: any) => {
      const container = containerRef.current;
      switch (type) {
        case 'splitInfo':
          paymentSession.mountWidget(container, {
            widgetType: YaPayInstance.WidgetType.BnplPreview,
          });
          break;
        case 'yandexCard':
          paymentSession.mountButton(container, {
            type: YaPayInstance.ButtonType.Pay,
            width: YaPayInstance.ButtonWidth.Max,
            theme: YaPayInstance.ButtonTheme.Black,
          });
          break;
        case 'yandexSplit':
          paymentSession.mountWidget(container, {
            widgetType: YaPayInstance.WidgetType.BnplRequired,
            borderRadius: 4,
          });
          break;
        default:
          paymentSession.mountWidget(container, {
            widgetType: YaPayInstance.WidgetType.BnplRequired,
            borderRadius: 4,
          });
      }
    },
    [YaPayInstance, type],
  );

  useEffect(() => {
    let session = null;
    let timeout: NodeJS.Timeout | null = null;

    if (YaPayInstance) {
      const paymentData: PaymentData = {
        env: isProduction ? YaPayInstance.PaymentEnv.Production : YaPayInstance.PaymentEnv.Sandbox,
        version: 4,
        currencyCode: currency,
        merchantId: yandexMerchantId,
        totalAmount,
        availablePaymentMethods,
      };

      // Создаем платежную сессию
      try {
        YaPayInstance.createSession(paymentData, {
          onPayButtonClick: onPayButtonClick,
          onFormOpenError: handleError,
        })
          .then((paymentSession) => {
            session = paymentSession;
            // Показываем кнопку Yandex Pay на странице.
            mountCorrectComponent(paymentSession);

            //даем время виджету появиться на странице прежде чем убрать лоадер
            timeout = setTimeout(() => setIsMounted(true), 2000);
          })
          .then(() => {
            const widget = document.querySelector('.ya-pay-widget');
            widgetRef.current = widget;
          })
          .catch(() => {
            // Не получилось создать платежную сессию.
            handleError();
          });
      } catch {
        handleError();
      }
    }

    return () => {
      clearTimeout(timeout);
      session?.destroy();
      session = null;
    };
  }, [
    YaPayInstance,
    currency,
    isProduction,
    onPayButtonClick,
    totalAmount,
    mountCorrectComponent,
    availablePaymentMethods,
    yandexMerchantId,
  ]);

  useEffect(() => {
    const widget = widgetRef.current;

    if (!isMounted || !widget) return;

    const resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
      if (!Array.isArray(entries) || entries.length === 0) return;

      const [entry] = entries;
      const element = entry.target as HTMLDivElement;

      if (
        element.style.visibility !== 'hidden' &&
        element.style.display !== 'none' &&
        element.style.height !== '0px'
      ) {
        setSplitUnavailable(false);
      } else {
        setSplitUnavailable(true);
      }
    });

    resizeObserver.observe(widget);

    return () => resizeObserver.unobserve(widget);
  }, [isMounted]);

  return (
    <div className={className} {...restProps}>
      <div className={cn(styles.container, { [styles.infoContainer]: isInfoWidgetAvailable })}>
        {isInfoWidgetAvailable && <div className={styles.whiteBox}></div>}
        {splitUnavailable && splitUnavailableWarning}
        {!isMounted && !splitUnavailable && (
          <SkeletRect
            className={cn(cnSkeleton, {
              [styles.skeletonButton]: isButton,
              [styles.skeleton]: !isButton,
            })}
          >
            <SkeletWave />
          </SkeletRect>
        )}
        <div
          ref={containerRef}
          style={{
            visibility: !isWidgetVisible ? 'hidden' : 'visible',
            height: !isWidgetVisible ? '0' : 'auto',
          }}
          className={styles.widgetWrapper}
        ></div>
      </div>

      {additionalPeriod && !splitUnavailable && (
        <AdditionalPeriod
          className={cn(styles.additionalPeriod, { [styles.isHidden]: !isMounted })}
          icon={additionalPeriod.icon}
          onChevronClick={additionalPeriod.onChevronClick}
          variant={additionalPeriod.variant}
          totalAmount={additionalPeriod.totalAmount}
        />
      )}
    </div>
  );
};

export default YandexSplitWidget;
